import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faCalendar, faChartBar, faArrowLeft, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

function TowerShipmentReport() {
  const { towerId } = useParams();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [stats, setStats] = useState({
    total_paquetes: 0,
    paquetes_entregados: 0,
    paquetes_pendientes: 0
  });
  const [towerInfo, setTowerInfo] = useState(null);
  const [packages, setPackages] = useState([]);

  const fetchData = async () => {
    try {
      const [statsResponse, towerResponse, packagesResponse] = await Promise.all([
        axios.get(`${BASE_URL}/packageStatsByTower/${towerId}?startDate=${startDate}&endDate=${endDate}`),
        axios.get(`${BASE_URL}/tower/${towerId}`),
        axios.get(`${BASE_URL}/packagesByTower/${towerId}?startDate=${startDate}&endDate=${endDate}`)
      ]);
      
      setStats(statsResponse.data);
      setTowerInfo(towerResponse.data[0]);
      setPackages(packagesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [towerId, startDate, endDate]);

  // Datos para el gráfico de pastel (Entregados vs Pendientes)
  const pieData = [
    { name: 'Entregados', value: stats.paquetes_entregados, color: '#10b981' },
    { name: 'Pendientes', value: stats.paquetes_pendientes, color: '#f59e0b' }
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex items-center justify-center mb-10">
            <button
              onClick={() => navigate('/shipmentReport')}
              className="absolute left-8 bg-white hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg shadow-md flex items-center gap-2 transition-all"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
              Volver
            </button>
            <FontAwesomeIcon icon={faBuilding} className="h-10 w-10 text-primary-600 mr-3" />
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-800">
                {towerInfo ? towerInfo.nombre : 'Cargando...'}
              </h1>
              <p className="text-gray-600 mt-2">Reporte de Paquetería</p>
            </div>
          </div>

          {/* Selector de Fechas */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faCalendar} className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-800">Rango de Fechas</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Estadísticas Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Total Paquetes</p>
                  <p className="text-4xl font-bold">{stats.total_paquetes}</p>
                </div>
                <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-blue-300 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium mb-1">Entregados</p>
                  <p className="text-4xl font-bold">{stats.paquetes_entregados}</p>
                </div>
                <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-green-300 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium mb-1">Pendientes</p>
                  <p className="text-4xl font-bold">{stats.paquetes_pendientes}</p>
                </div>
                <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-orange-300 opacity-50" />
              </div>
            </div>
          </div>

          {/* Gráfico de Pastel - Estado de Paquetes */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faChartBar} className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-800">Estado de Paquetes</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla de Paquetes */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faBox} className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-800">Detalle de Paquetes</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Recepción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Apartamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inquilino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descripción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Entrega
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {packages.map((pkg) => (
                    <tr key={pkg.id_paquete} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(pkg.fecha_recepcion), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pkg.apartamento_numero}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pkg.inquilino_nombre}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {pkg.descripcion}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {pkg.estado_entrega === 'Entregado' ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Entregado
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pkg.fecha_entrega 
                          ? format(new Date(pkg.fecha_entrega), 'dd/MM/yyyy HH:mm')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {packages.length === 0 && (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hay paquetes en el rango seleccionado</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TowerShipmentReport;

